import { colors, createTheme } from "@mui/material";
// import { FormHelperText } from '@material-ui/core';

declare module "@mui/material/styles" {
  interface TypeBackground {
    subtle: string;
    tableRowHover: string;
  }
  interface Palette {
    centreOfGravity: {
      danger: string;
    };
    customerStatus: {
      draft: string;
      active: string;
      inactive: string;
    };
    priceQuoteStatus: {
      draft: string;
      sent: string;
      accepted: string;
    };
    financialStatus: {
      PAID: string;
      SETTLED: string;
      OVERDUE: string;
    };
    workOrderStatus: {
      WAITING_ENTRY: string;
      ARRIVED_YARD: string;
      CHASSIS_PREP: string;
      READY_PRODUCTION: string;
      IN_PRODUCTION: string;
      PROD_TO_WELD: string;
      IN_WELDING: string;
      WELD_TO_GRIND: string;
      IN_GRINDING: string;
      GRIND_TO_PAINT: string;
      IN_PAINT: string;
      PAINT_TO_FINISH: string;
      IN_FINISHES: string;
      DONE_NO_CURTAIN: string;
      DONE_TEST_READY: string;
      RELEASE_READY: string;
    };
    profile: {
      avatarBackground: string;
      avatarText: string;
    };
    login: {
      heading: string;
      brand: string;
      link: string;
      autofill: string;
    };
  }
  interface PaletteOptions {
    centreOfGravity?: {
      danger?: string;
    };
    customerStatus?: {
      draft?: string;
      active?: string;
      inactive?: string;
    };
    priceQuoteStatus?: {
      draft?: string;
      sent?: string;
      accepted?: string;
    };
    financialStatus?: {
      PAID?: string;
      SETTLED?: string;
      OVERDUE?: string;
    };
    workOrderStatus?: {
      WAITING_ENTRY?: string;
      ARRIVED_YARD?: string;
      CHASSIS_PREP?: string;
      READY_PRODUCTION?: string;
      IN_PRODUCTION?: string;
      PROD_TO_WELD?: string;
      IN_WELDING?: string;
      WELD_TO_GRIND?: string;
      IN_GRINDING?: string;
      GRIND_TO_PAINT?: string;
      IN_PAINT?: string;
      PAINT_TO_FINISH?: string;
      IN_FINISHES?: string;
      DONE_NO_CURTAIN?: string;
      DONE_TEST_READY?: string;
      RELEASE_READY?: string;
    };
    profile?: {
      avatarBackground?: string;
      avatarText?: string;
    };
    login?: {
      heading?: string;
      brand?: string;
      link?: string;
      autofill?: string;
    };
  }
}


export const HEADER_FONT_FAMILY =
  '"Mona-Sans"';
export const BODY_FONT_FAMILY = "Noto Sans Hebrew";

export const headerFontFamily = HEADER_FONT_FAMILY;
export const bodyFontFamily = BODY_FONT_FAMILY;

export const LOGIN_HEADING_COLOR = "#2A3333";
export const LOGIN_BRAND_COLOR = "#49B1A8";
export const LOGIN_LINK_COLOR = "#012497";
export const LOGIN_AUTOFILL_COLOR = "#F7F7FA";
export const COG_DANGER_COLOR = "#C02D2D";
export const SUCCESS_STATUS_COLOR = "#169641";
export const TABLE_ROW_HOVER_COLOR = "#F8FAFF";
export const CUSTOMER_DRAFT_STATUS_COLOR = "#8A909B";
export const QUOTE_SENT_STATUS_COLOR = "#45BBD9";
export const QUOTE_ACCEPTED_STATUS_COLOR = "#50B98F";
export const FINANCIAL_SETTLED_STATUS_COLOR = "#3B82F6";
export const FINANCIAL_OVERDUE_STATUS_COLOR = "#C5090C";
export const WORK_ORDER_STATUS_COLORS = {
  WAITING_ENTRY: "#8E9FB1",
  ARRIVED_YARD: "#3B82F6",
  CHASSIS_PREP: "#6366F1",
  READY_PRODUCTION: "#8B5CF6",
  IN_PRODUCTION: "#F97316",
  PROD_TO_WELD: "#EA580C",
  IN_WELDING: "#DC2626",
  WELD_TO_GRIND: "#E11D48",
  IN_GRINDING: "#D946EF",
  GRIND_TO_PAINT: "#A855F7",
  IN_PAINT: "#7C3AED",
  PAINT_TO_FINISH: "#6366F1",
  IN_FINISHES: "#0EA5E9",
  DONE_NO_CURTAIN: "#06B6D4",
  DONE_TEST_READY: "#14B8A6",
  RELEASE_READY: "#22C55E",
} as const;

const theme = createTheme({
  direction: "ltr",
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            pointerEvents: "auto", // Corrected value
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            pointerEvents: "auto", // Corrected value
          },
        },
      },
    },
  },
  palette: {
    background: {
      default: "#F5F8FF",
      paper: colors.common.white,
      subtle: "#FBFDFF",
      tableRowHover: TABLE_ROW_HOVER_COLOR,
    },
    primary: {
      main: "#5A8DF1",
    },
    secondary: {
      main: "#253858",
    },
    grey: {
      400: "#F4F5F7",
    },
    error: {
      main: "#C5090C",
    },
    success: {
      main: SUCCESS_STATUS_COLOR,
      400: SUCCESS_STATUS_COLOR,
    },
    text: {
      primary: "#79808A",
      secondary: colors.blueGrey[600],
      disabled: "#BDC0C3",
    },

    divider: "#E9ECF2",

    info: {
      main: "#5A8DF1",
      light: "#EFF4FE",
    },
    centreOfGravity: {
      danger: COG_DANGER_COLOR,
    },
    customerStatus: {
      draft: CUSTOMER_DRAFT_STATUS_COLOR,
      active: SUCCESS_STATUS_COLOR,
      inactive: CUSTOMER_DRAFT_STATUS_COLOR,
    },
    priceQuoteStatus: {
      draft: CUSTOMER_DRAFT_STATUS_COLOR,
      sent: QUOTE_SENT_STATUS_COLOR,
      accepted: QUOTE_ACCEPTED_STATUS_COLOR,
    },
    financialStatus: {
      PAID: SUCCESS_STATUS_COLOR,
      SETTLED: FINANCIAL_SETTLED_STATUS_COLOR,
      OVERDUE: FINANCIAL_OVERDUE_STATUS_COLOR,
    },
    workOrderStatus: WORK_ORDER_STATUS_COLORS,
    profile: {
      avatarBackground: "#FADADD",
      avatarText: "#1E2A44",
    },
    login: {
      heading: LOGIN_HEADING_COLOR,
      brand: LOGIN_BRAND_COLOR,
      link: LOGIN_LINK_COLOR,
      autofill: LOGIN_AUTOFILL_COLOR,
    },
  },
  typography: {
    fontFamily: bodyFontFamily,
    allVariants: {
      textTransform: "none",
    },
  },
});

export default theme;
